import React, { useEffect } from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import projects from '../../data/projectsData';

const Projects = () => {
  useEffect(() => {
    document.title = 'Projects — Dmitri Malisev';
  }, []);

  return (
    <Container>
      <PageHeader>
        <SectionLabel>Projects</SectionLabel>
        <PageTitle>Hobby Projects (Pet projects)</PageTitle>
        <PageSubtitle>Personal projects and experiments.</PageSubtitle>
      </PageHeader>

      {projects.length === 0 ? (
        <ComingSoon>
          <ComingSoonMono>{'// coming soon'}</ComingSoonMono>
          <ComingSoonText>Projects are being added. Check back later.</ComingSoonText>
        </ComingSoon>
      ) : (
        <ProjectGrid>
          {[...projects]
            .sort((a, b) => {
              if (a.featured && !b.featured) return -1;
              if (!a.featured && b.featured) return 1;
              if (a.featured && b.featured) return (a.order || 0) - (b.order || 0);
              return 0;
            })
            .map(project => (
              <ProjectCard key={project.slug} to={`/projects/${project.slug}`}>
                <ProjectTop>
                  <ProjectTitle>{project.title}</ProjectTitle>
                  {project.featured && <FeaturedBadge>featured</FeaturedBadge>}
                </ProjectTop>
                <ProjectDesc>{project.shortDescription}</ProjectDesc>
                <ProjectTags>
                  {project.tags.map(tag => <ProjectTag key={tag}>{tag}</ProjectTag>)}
                </ProjectTags>
              </ProjectCard>
            ))}
        </ProjectGrid>
      )}
    </Container>
  );
};

const Container = styled.div`
  padding: 4rem 0;
`;

const PageHeader = styled.div`
  margin-bottom: 3rem;
`;

const SectionLabel = styled.p`
  font-family: var(--font-mono);
  font-size: 0.7rem;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.14em;
  color: var(--accent);
  margin: 0 0 0.5rem;
`;

const PageTitle = styled.h1`
  font-size: 1.75rem;
  font-weight: 700;
  color: var(--text-primary);
  margin: 0 0 0.5rem;
  letter-spacing: -0.02em;

  @media (max-width: 600px) {
    font-size: 1.375rem;
  }
`;

const PageSubtitle = styled.p`
  font-size: 1rem;
  color: var(--text-primary);
  margin: 0;
`;

const ComingSoon = styled.div`
  padding: 3rem 0;
`;

const ComingSoonMono = styled.p`
  font-family: var(--font-mono);
  font-size: 0.9375rem;
  color: var(--accent);
  margin: 0 0 0.75rem;
`;

const ComingSoonText = styled.p`
  font-size: 1rem;
  color: var(--text-muted);
  margin: 0;
`;

const ProjectGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 1rem;
`;

const ProjectCard = styled(Link)`
  display: flex;
  flex-direction: column;
  padding: 1.5rem;
  border: 1px solid var(--border);
  border-radius: 6px;
  background: var(--bg-surface);
  text-decoration: none;
  transition: border-color 0.2s;

  &:hover {
    border-color: var(--accent);
  }
`;

const ProjectTop = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  margin-bottom: 0.5rem;
`;

const ProjectTitle = styled.h2`
  font-size: 1rem;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0;
`;

const FeaturedBadge = styled.span`
  font-family: var(--font-mono);
  font-size: 0.65rem;
  padding: 0.1rem 0.4rem;
  border: 1px solid var(--accent);
  border-radius: 3px;
  color: var(--accent);
  letter-spacing: 0.05em;
`;

const ProjectDesc = styled.p`
  font-size: 0.875rem;
  color: var(--text-muted);
  line-height: 1.6;
  margin: 0 0 1rem;
  flex: 1;
`;

const ProjectTags = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.375rem;
`;

const ProjectTag = styled.span`
  font-family: var(--font-mono);
  font-size: 0.7rem;
  padding: 0.15rem 0.45rem;
  border: 1px solid var(--border);
  border-radius: 3px;
  color: var(--text-muted);
`;

export default Projects;
